import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";
import DateRangePicker from "../../components/common/DateRangePicker";
import { financialRecordsApi } from "../../services/api";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { deriveStatus } from "../contacts/status";
import type {
  FinancialRecord,
  FinancialRecordType,
  FinancialRecordStatus,
} from "@akfdev-com/shared/src/models";

type TabType = "all" | FinancialRecordType;

const TAB_LABELS: Record<TabType, string> = {
  all: "All",
  invoice: "Invoices",
  bill: "Bills",
  payment: "Service Charge Payments",
  service_charge: "Service Charges",
};

const TYPE_BADGE: Record<FinancialRecordType, string> = {
  invoice: "bg-blue-100 text-blue-700",
  bill: "bg-purple-100 text-purple-700",
  payment: "bg-teal-100 text-teal-700",
  service_charge: "bg-orange-100 text-orange-700",
};

const STATUS_BADGE: Record<FinancialRecordStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  paid: "bg-green-100 text-green-700",
  partial: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

/**
 * Extra badge classes for statuses that don't appear on FinancialRecordStatus
 * but ARE emitted by `deriveStatus` when we compute status live from
 * (amountPaid, amount, dueDate). `overdue` in particular is a derived state
 * — the stored `status` field never holds this value; it's derived per-
 * render. Matches the shape used by
 * `frontend/src/pages/contacts/FinancialRecordsTable.tsx`.
 */
const DERIVED_STATUS_BADGE: Record<string, string> = {
  ...STATUS_BADGE,
  overdue: "bg-red-100 text-red-700",
};

interface Filters {
  search: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
}

const FinancialRecordsList: React.FC = () => {
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [filtered, setFiltered] = useState<FinancialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [filters, setFilters] = useState<Filters>({
    search: "",
    status: "",
    startDate: null,
    endDate: null,
  });

  const { currentBuilding } = useBuilding();

  useEffect(() => {
    const fetchRecords = async () => {
      if (!currentBuilding?.id) return;
      try {
        setLoading(true);
        const data = await financialRecordsApi.getAll(currentBuilding.id);
        const items: FinancialRecord[] = Array.isArray(data) ? data : [];
        setRecords(items);
      } catch (err) {
        console.error("Error fetching financial records:", err);
        setError("Failed to load financial records. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, [currentBuilding?.id]);

  useEffect(() => {
    let result = [...records];

    if (activeTab !== "all") {
      result = result.filter((r) => r.type === activeTab);
    }

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (r) =>
          r.description?.toLowerCase().includes(term) ||
          r.supplierName?.toLowerCase().includes(term) ||
          r.title?.toLowerCase().includes(term) ||
          r.contactName?.toLowerCase().includes(term) ||
          r.unitName?.toLowerCase().includes(term) ||
          r.reference?.toLowerCase().includes(term) ||
          r.invoiceNumber?.toLowerCase().includes(term),
      );
    }

    if (filters.status) {
      result = result.filter((r) => r.status === filters.status);
    }

    if (filters.startDate) {
      result = result.filter((r) => r.date >= filters.startDate!);
    }

    if (filters.endDate) {
      result = result.filter((r) => r.date <= filters.endDate!);
    }

    // Sort newest first (fallback to createdAt or empty string if date is missing)
    result.sort((a, b) => {
      const aDate = a.date || a.createdAt || "";
      const bDate = b.date || b.createdAt || "";
      return bDate.localeCompare(aDate);
    });
    setFiltered(result);
  }, [records, activeTab, filters]);

  const handleFilterChange = (name: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (start: string | null, end: string | null) => {
    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const handleClearFilters = () => {
    setFilters({ search: "", status: "", startDate: null, endDate: null });
  };

  const displayLabel = (r: FinancialRecord): string => {
    if (r.type === "invoice")
      return r.supplierName || r.invoiceNumber || "Invoice";
    if (r.type === "bill") return r.title || r.description || "Bill";
    return r.unitName || r.paymentNumber || "Service Charge";
  };

  const contextLabel = (r: FinancialRecord): string | undefined => {
    if (r.type === "payment") return r.unitName;
    return r.contactName || r.supplierName;
  };

  const total = filtered.reduce((sum, r) => sum + r.amount, 0) / 100;

  if (loading) return <Spinner message="Loading financial records..." />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div>
      <PageHeader
        title="Financial Records"
        description={`Invoices, bills and service charge payments for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <Link
            to="/dashboard/financial-records/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Add Record
          </Link>
        }
      />

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          {(
            ["all", "invoice", "bill", "payment", "service_charge"] as TabType[]
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {TAB_LABELS[tab]}
              <span className="ml-2 text-xs text-gray-400">
                (
                {tab === "all"
                  ? records.length
                  : records.filter((r) => r.type === tab).length}
                )
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="mt-4 bg-white shadow px-4 py-4 sm:rounded-lg sm:p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <SearchInput
              placeholder="Search records..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onClear={() => handleFilterChange("search", "")}
            />
          </div>
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="block w-full text-sm border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="flex items-center">
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        </div>
        {(filters.search ||
          filters.status ||
          filters.startDate ||
          filters.endDate) && (
          <div className="mt-2 text-right">
            <button
              onClick={handleClearFilters}
              className="text-sm text-primary-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      {filtered.length > 0 && (
        <div className="mt-3 text-sm text-gray-500 text-right">
          {filtered.length} record{filtered.length !== 1 ? "s" : ""} · Total:{" "}
          <span className="font-semibold text-gray-900">
            {formatCurrency(total)}
          </span>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          title="No financial records"
          description="Add an invoice, bill or service charge payment to get started."
        />
      ) : (
        <div className="mt-3 bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Contact / Unit
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Reconciled
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          TYPE_BADGE[r.type]
                        }`}
                      >
                        {r.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      <Link
                        to={`/dashboard/financial-records/${r.id}?buildingId=${r.buildingId}`}
                        className="hover:text-primary-600"
                      >
                        {displayLabel(r)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {contextLabel(r) || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {formatDate(r.date)}
                    </td>
                    <td className="px-4 py-3">
                      {/* Status is derived live from
                          (amountPaid, amount, dueDate) rather than
                          reading the stored `r.status` verbatim
                          (crossref-bug Slice 2c). This matches the
                          contacts-page FinancialRecordsTable behaviour
                          and stays correct for records imported BEFORE
                          crossref-bug Slice 2a landed (whose stored
                          status may still read 'pending' but whose
                          amountPaid now equals amount). Fully-paid
                          records show 'paid' even when past due — the
                          precedence rule from
                          `frontend/src/pages/contacts/status.ts`. */}
                      {(() => {
                        const derived = deriveStatus(
                          r.amountPaid || 0,
                          r.amount || 0,
                          r.dueDate || new Date().toISOString(),
                          new Date(),
                        );
                        return (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              DERIVED_STATUS_BADGE[derived] ??
                              "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {derived}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                      {formatCurrency(r.amount / 100)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {/* Reconciled cell — clickable Link when the FR is
                          linked (crossref-bug Slice 2c). Opens the FR
                          detail page which now lazy-fetches the linked
                          bank tx and renders a "View transaction" link.
                          Keeps the two sides of the cross-reference
                          navigable in one click. */}
                      {r.bankTransactionId ? (
                        <Link
                          to={`/dashboard/financial-records/${r.id}?buildingId=${r.buildingId}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200"
                        >
                          Yes →
                        </Link>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          No
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialRecordsList;
